import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

import { useDonationContextProvider } from 'context/DonationContextProvider';
import { useBranchContext } from 'context/BranchContext';
import { toast } from 'react-toastify';
import { v4 as uuid } from 'uuid';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import {
  PAYMENT_OPTION_MONTHLY,
  PAYMENT_OPTION_ONE_OFF
} from 'components/donations/donation-packages/package-form/PackageForm';

import classNames from 'classnames';
import { FormProvider } from 'components/common/form';
import { formatMoney } from 'helpers/utils';
import { CustomDonationCheck } from './OptionButton';
import { CartIcon } from 'assets/icons/cart';
import {
  getPackages,
  getDonationTypes,
  getWebsiteSettings,
  getUrgentAppealsWithPackages,
  getDonationLinkBySlug
} from '../../lib/supabaseQueries';

function QuickDonationForm() {
  // First path segment, e.g. /ramadan -> a donation_links slug (see Layout)
  const { linkSlug } = useParams();
  // const [isMonthlySupportSectionVisible, setIsMonthlySupportSectionVisible] =
  //   useState(true);
  const [isCustomAmountSelected, setIsCustomAmountSelected] = useState(false);
  const [packages, setPackages] = useState({ data: null, loading: true, error: null });
  const [activeTooltip, setActiveTooltip] = useState(null);

  const customAmountInputRef = useRef(null);

  const organizationId = process.env.REACT_APP_ORGANIZATION_ID;
  const { branchId, loading: branchLoading } = useBranchContext(); // Get branch from subdomain

  const { addDonationToBasket } = useDonationContextProvider();

  const form = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      paymentType: null,
      isTenMonthlySupportChecked: false,
      donationType: null,
      packageId: null,
      donationAmount: {
        id: null,
        value: null
      }
    },
    resolver: yupResolver(quickDonationSchema)
  });
  const {
    watch,
    setValue,
    formState: { errors },
    handleSubmit
  } = form;
  const paymentType = watch('paymentType');
  const selectedDonationTypeId = watch('donationType');
  const donationAmount = watch('donationAmount');
  const packageId = watch('packageId');
  const values = watch();

  // Fetch data from Supabase
  useEffect(() => {
    const fetchData = async () => {
      setPackages(prev => ({ ...prev, loading: true }));

      try {
        // Fetch packages, donation types, and website settings in parallel
        // Filter by branch from subdomain
        const [packagesResult, donationTypesResult, websiteSettingsResult, urgentAppealsResult, linkResult] = await Promise.all([
          getPackages(organizationId, branchId, {}),
          getDonationTypes(organizationId, branchId),
          getWebsiteSettings(organizationId, branchId),
          getUrgentAppealsWithPackages(organizationId, branchId),
          linkSlug
            ? getDonationLinkBySlug(organizationId, linkSlug, branchId)
            : Promise.resolve({ data: null, error: null })
        ]);

        if (packagesResult.error || donationTypesResult.error || websiteSettingsResult.error) {
          setPackages({
            data: null,
            loading: false,
            error: packagesResult.error || donationTypesResult.error || websiteSettingsResult.error
          });
          return;
        }

        // An unknown or inactive slug (or a lookup failure) just means the
        // normal page with no preselection.
        if (linkResult.error) {
          console.error('Error resolving donation link:', linkResult.error);
        }
        const link = linkResult.data || null;

        // Build a set of package IDs that are used by urgent appeals so we
        // can exclude them from the quick-donation campaign dropdown. A
        // package reached via a campaign link stays in regardless.
        const urgentAppealPackageIds = new Set(
          (urgentAppealsResult.data || [])
            .map(appeal => appeal.package_id)
            .filter(Boolean)
        );

        const allPackages = packagesResult.data || [];
        const filteredPackages = allPackages.filter(
          pkg => !urgentAppealPackageIds.has(pkg.id) || pkg.id === link?.package_id
        );

        setPackages({
          data: {
            packages: filteredPackages,
            donation_type: donationTypesResult.data || [],
            website_settings: websiteSettingsResult.data ? [websiteSettingsResult.data] : [],
            link
          },
          loading: false,
          error: null
        });
      } catch (error) {
        console.error('Error fetching donation data:', error);
        setPackages({
          data: null,
          loading: false,
          error: error.message
        });
      }
    };

    // Wait for branch to load before fetching packages
    if (organizationId && !branchLoading) {
      fetchData();
    }
  }, [organizationId, branchId, branchLoading]);

  const packageOptions = useMemo(() => {
    if (!packages.data) return [];

    const filteredPackages = packages.data.packages.filter(packageItem => {
      const matchesPaymentType =
        packageItem.payment_options &&
        packageItem.payment_options.includes(paymentType);

      // When a donation type is selected, only show campaigns that include it.
      if (selectedDonationTypeId) {
        const donationTypesForPackage = packageItem.donation_type || [];
        const matchesDonationType =
          Array.isArray(donationTypesForPackage) &&
          donationTypesForPackage.includes(selectedDonationTypeId);

        return matchesPaymentType && matchesDonationType;
      }

      return matchesPaymentType;
    });

    return filteredPackages.map(({ id, package_title }) => ({
      label: package_title,
      value: id
    }));
  }, [packages.data, paymentType, selectedDonationTypeId]);

  const donationTypeOptions = useMemo(() => {
    if (!packages?.data) return [];

    const baseOptions =
      packages.data.donation_type?.map(({ id, donation_type_name }) => ({
        label: donation_type_name,
        value: id
      })) || [];

    // Build a set of donation type IDs that actually have at least one
    // campaign for the currently selected payment type.
    const donationTypeIdsWithCampaigns = new Set();

    packages.data.packages?.forEach(packageItem => {
      if (
        !packageItem.payment_options ||
        !packageItem.payment_options.includes(paymentType)
      ) {
        return;
      }

      (packageItem.donation_type || []).forEach(donationTypeId => {
        donationTypeIdsWithCampaigns.add(donationTypeId);
      });
    });

    // If we didn't find any mapping info (e.g. legacy data), fall back to
    // showing all donation types so the UI still works.
    if (!donationTypeIdsWithCampaigns.size) {
      return baseOptions;
    }

    return baseOptions.filter(option =>
      donationTypeIdsWithCampaigns.has(option.value)
    );
  }, [packages, paymentType]);

  const selectedPackage = useMemo(() => {
    return packages.data?.packages.find(
      packageItem => packageItem.id === packageId
    );
  }, [packages.data, packageId]);
  // A package can only be selected if it is offered for the payment type and
  // (when one is chosen) the donation type in play.
  const isPackageOffered = (packageItem, currentPaymentType, donationTypeId) =>
    !!packageItem &&
    !!packageItem.payment_options?.includes(currentPaymentType) &&
    (!donationTypeId ||
      (Array.isArray(packageItem.donation_type) &&
        packageItem.donation_type.includes(donationTypeId)));

  // Preference order for the campaign: the one a campaign link (/<slug>)
  // asked for, then the website-settings default, then the first offered.
  const pickPackageId = (currentPaymentType, donationTypeId) => {
    const { packages: packageItems, website_settings, link } = packages.data;
    const preferred = [link?.package_id, website_settings[0]?.default_package_id]
      .filter(Boolean)
      .map(id => packageItems.find(packageItem => packageItem.id === id))
      .find(packageItem => isPackageOffered(packageItem, currentPaymentType, donationTypeId));
    const fallback = packageItems.find(packageItem =>
      isPackageOffered(packageItem, currentPaymentType, donationTypeId)
    );
    return preferred?.id || fallback?.id || '';
  };

  useEffect(() => {
    if (!packages?.data) return;

    const currentPackage = packages.data.packages.find(
      packageItem => packageItem.id === packageId
    );

    if (isPackageOffered(currentPackage, paymentType, selectedDonationTypeId)) return;

    setValue('packageId', pickPackageId(paymentType, selectedDonationTypeId));
  }, [packages, packageId, paymentType, selectedDonationTypeId, setValue]);

  const donationAmountOptions = selectedPackage?.donation_amount
    ? [...selectedPackage?.donation_amount].sort((a, b) => {
        if (a === 'custom') return 1;
        if (b === 'custom') return -1;
        return a - b;
      })
    : [];

  // Preselect Custom amount when campaign changes, or the only option if just one
  useEffect(() => {
    if (!selectedPackage) return;

    const amounts = [...(selectedPackage.donation_amount || [])].sort((a, b) => {
      if (a === 'custom') return 1;
      if (b === 'custom') return -1;
      return a - b;
    });

    const customIndex = amounts.indexOf('custom');

    if (customIndex !== -1) {
      setIsCustomAmountSelected(true);
      setValue('donationAmount', { id: customIndex, value: null });
    } else if (amounts.length === 1) {
      setIsCustomAmountSelected(false);
      setValue('donationAmount', { id: 0, value: amounts[0] });
    } else {
      setIsCustomAmountSelected(false);
      setValue('donationAmount', { id: null, value: null });
    }
  }, [packageId]);

  const isOneOffPaymentTypeSelected = paymentType === PAYMENT_OPTION_ONE_OFF;
  const isMonthlyPaymentTypeSelected = paymentType === PAYMENT_OPTION_MONTHLY;

  const resetForm = () => {
    const { link, website_settings } = packages.data;
    const linkPackage = packages.data.packages.find(
      packageItem => packageItem.id === link?.package_id
    );

    setValue('isTenMonthlySupportChecked', false);
    setIsCustomAmountSelected(false);

    // Landing via a campaign link: pick a payment type the linked campaign
    // offers so the preselected campaign is visible straight away (the
    // campaign list is empty until a payment type is chosen). The visitor's
    // own choice is kept on later resets.
    let currentPaymentType = paymentType;
    if (!currentPaymentType && linkPackage?.payment_options?.length) {
      currentPaymentType = linkPackage.payment_options.includes(PAYMENT_OPTION_ONE_OFF)
        ? PAYMENT_OPTION_ONE_OFF
        : linkPackage.payment_options[0];
      setValue('paymentType', currentPaymentType);
    }

    // Donation type: the link's own, else one the linked campaign offers
    // (preferring the settings default), else the settings default.
    const settingsDonationTypeId = website_settings[0]?.default_donation_type_id;
    const linkPackageTypes = Array.isArray(linkPackage?.donation_type)
      ? linkPackage.donation_type
      : [];
    const donationTypeId =
      link?.donation_type_id ||
      (linkPackage &&
        (linkPackageTypes.includes(settingsDonationTypeId)
          ? settingsDonationTypeId
          : linkPackageTypes[0])) ||
      settingsDonationTypeId ||
      donationTypeOptions[0]?.value;
    setValue('donationType', donationTypeId);

    // The campaign is a native <select> whose options depend on the payment
    // and donation type set just above, so they don't exist in the DOM until
    // the next render; setting the value now would be dropped by the browser
    // (it then displays the first option). Clear it instead and let the
    // "keep the campaign valid" effect pick the preferred one once the
    // options are rendered; the amount effect follows from that.
    setValue('packageId', '');
    setValue('donationAmount', { id: null, value: null });
  };

  const onAddDonation = () => {
    const donationTypeId = values.donationType;
    const packageName = packageOptions.find(
      option => option.value === values.packageId
    )?.label;

    // Get branch_id from selected package
    const selectedPkg = packages.data?.packages.find(
      pkg => pkg.id === values.packageId
    );

    addDonationToBasket({
      ...values,
      packageId: values.packageId,
      packageName,
      branchId: selectedPkg?.branch_id || null, // Add branch_id to basket item
      transactionFee: selectedPkg?.transaction_fee || null, // transaction_fee from joined table
      donationType: {
        id: donationTypeId,
        name: donationTypeOptions.find(
          option => option.value === donationTypeId
        )?.label
      },
      count: 1,
      id: uuid()
    });
    resetForm();
    toast.success('Donation added to the basket.');
  };

  useEffect(() => {
    if (isCustomAmountSelected) {
      customAmountInputRef.current?.focus();
    }
  }, [isCustomAmountSelected]);

  useEffect(() => {
    if (packages?.data) {
      resetForm();
    }
  }, [packages]);

  return (
    <FormProvider methods={form} onSubmit={handleSubmit(onAddDonation)}>
      <div className="shadow-sm rounded-3 border border-2 bg-wr-donation-grey-0 wr-donation-text-grey-900 p-2 d-flex flex-column gap-3">
        <div className="container p-0 pt-2">
          {!paymentType && (
            <span className="wr-donation-body4 fw-bold text-black d-inline-block mb-2">
              Please select One Off or Monthly
            </span>
          )}
          <div className="row g-2">
            <div className="col">
              <div
                className="position-relative"
                onMouseEnter={() => setActiveTooltip('oneoff')}
                onMouseLeave={() => setActiveTooltip(null)}
              >
                <CustomDonationCheck
                  onClick={() => {
                    setValue('paymentType', PAYMENT_OPTION_ONE_OFF);
                    setValue('isTenMonthlySupportChecked', false);
                  }}
                  checked={isOneOffPaymentTypeSelected}
                >
                  One Off
                </CustomDonationCheck>
                {activeTooltip === 'oneoff' && (
                  <div
                    className="position-absolute bg-dark text-white rounded p-2"
                    style={{
                      bottom: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      marginBottom: '8px',
                      fontSize: '0.8rem',
                      whiteSpace: 'normal',
                      minWidth: '200px',
                      maxWidth: '280px',
                      zIndex: 1050,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                    }}
                  >
                    A single payment. That's it—nothing ongoing.
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        borderLeft: '6px solid transparent',
                        borderRight: '6px solid transparent',
                        borderTop: '6px solid #212529'
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="col">
              <div
                className="position-relative"
                onMouseEnter={() => setActiveTooltip('monthly')}
                onMouseLeave={() => setActiveTooltip(null)}
              >
                <CustomDonationCheck
                  onClick={() => {
                    setValue('paymentType', PAYMENT_OPTION_MONTHLY);
                  }}
                  checked={isMonthlyPaymentTypeSelected}
                >
                  Monthly
                </CustomDonationCheck>
                {activeTooltip === 'monthly' && (
                  <div
                    className="position-absolute bg-dark text-white rounded p-2"
                    style={{
                      bottom: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      marginBottom: '8px',
                      fontSize: '0.8rem',
                      whiteSpace: 'normal',
                      minWidth: '200px',
                      maxWidth: '280px',
                      zIndex: 1050,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                    }}
                  >
                    A monthly payment. We'll handle it automatically for you each month on the same date.
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        borderLeft: '6px solid transparent',
                        borderRight: '6px solid transparent',
                        borderTop: '6px solid #212529'
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          {errors.paymentType && (
            <small className="text-danger mt-1">
              {errors.paymentType.message}
            </small>
          )}
        </div>
        <div className="container p-0">
          <div className="row g-2">
            <div className="col-md-6 col-12">
              <Form.Group>
                <span className="wr-donation-body4 fw-bold text-black d-inline-block mb-2 me-1">
                  Donation Type
                </span>
                <Form.Select
                  as="select"
                  name="donationType"
                  {...form.register('donationType')}
                  isInvalid={!!errors.donationType}
                  style={{ height: '60px' }}
                  className="border border-1 border-wr-donation-primary fw-semi-bold"
                >
                  <option value="" disabled>
                    Select Donation Type
                  </option>
                  {donationTypeOptions?.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.donationType?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
            <div className="col-md-6 col-12">
              <Form.Group>
                <span className="wr-donation-body4 fw-bold text-black d-inline-block mb-2 me-1">
                  Campaign
                </span>
                <Form.Select
                  as="select"
                  name="packageId"
                  {...form.register('packageId')}
                  isInvalid={!!errors.packageId}
                  style={{ height: '60px' }}
                  className="border border-1 border-wr-donation-primary fw-semi-bold"
                >
                  <option value="" disabled>
                    Select Campaign
                  </option>
                  {packageOptions?.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.donationType?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>
        </div>

        <div>
          <span className="wr-donation-body4 fw-bold text-black d-inline-block mb-2 me-1">
            Donation Amount
          </span>
          {/* divider */}
          {/* <div
            style={{
              width: '50px',
              height: '2px',
              backgroundColor: 'red'
              // margin: '0 auto 30px'
            }}
            className="mx-auto mb-4 mt-3"
          /> */}
          <div className="container p-0">
            <div className="row g-2">
              {donationAmountOptions.map((amount, index) => (
                <div
                  className={classNames('', {
                    'col-md-2 col-6': amount !== 'custom',
                    col: amount === 'custom'
                  })}
                  key={`amount-${index}`}
                >
                  <CustomDonationCheck
                    onClick={() => {
                      if (amount === 'custom') {
                        setIsCustomAmountSelected(true);
                        setValue('donationAmount', { id: index, value: null });
                      } else {
                        setValue('donationAmount', {
                          id: index,
                          value: amount
                        });
                        setIsCustomAmountSelected(false);
                      }
                    }}
                    checked={amount === 'custom' ? isCustomAmountSelected : donationAmount?.value === amount}
                  >
                    {amount === 'custom'
                      ? '\u00A3 Other'
                      : formatMoney(amount)}
                  </CustomDonationCheck>
                </div>
              ))}
              {/* <div className="col">
                <CustomDonationCheck
                  onClick={() => {
                    setIsCustomAmountSelected(true);
                    setValue('donationAmount', { id: null, value: null });
                  }}
                  checked={isCustomAmountSelected}
                >
                  Custom Amount
                  {isCustomAmountSelected && (
                    <Form.Check
                      type="checkbox"
                      inline
                      className="ms-2 wr-donation-payment-type-checkbox"
                      checked
                    />
                  )}
                </CustomDonationCheck>
              </div> */}
            </div>
          </div>
          {isCustomAmountSelected && donationAmountOptions.includes('custom') && (
            <Form.Control
              ref={customAmountInputRef}
              className="mt-3"
              style={{
                height: '60px',
                maxWidth: '350px',
                MozAppearance: 'textfield'
              }}
              required
              type="number"
              placeholder="Custom Amount"
              onWheel={e => e.target.blur()}
              onChange={e => {
                setValue('donationAmount', {
                  ...donationAmount,
                  value: +e.target.value
                });
              }}
            />
          )}
          <style>
            {`
              input[type="number"]::-webkit-outer-spin-button,
              input[type="number"]::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
              }
            `}
          </style>
          {errors.donationAmount?.value && (
            <small className="text-danger">
              {errors.donationAmount.value.message}
            </small>
          )}
        </div>
        {/* <span className="wr-donation-body4 fw-bold text-black d-inline-block mb-2 me-1">
            Donation Type
          </span> */}

        <Button
          className="align-self-center px-6 py-3 fw-bold text-white bg-wr-donation-primary border-0 w-100 w-md-auto mb-2 d-flex align-items-center justify-content-center gap-2"
          type="submit"
        >
          <CartIcon />
          Add Donation
        </Button>
      </div>
    </FormProvider>
  );
}

const quickDonationSchema = Yup.object().shape({
  paymentType: Yup.string().nullable().required('Please select One Off or Monthly'),

  isTenMonthlySupportChecked: Yup.boolean(),

  donationType: Yup.string().required('Donation type is required'),

  packageId: Yup.string().required('Campaign is required'),

  donationAmount: Yup.object().shape({
    id: Yup.number().nullable(),
    value: Yup.number().nullable().required('Donation amount value is required')
  })
});

export { QuickDonationForm };
